import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useLang } from '../hooks/useLang';
import BookingSimulator from './BookingSimulator';
import './Waitlist.css';

const TIERS = { founding: 100, early_bird: 500 };

function getTier(position) {
  if (position <= TIERS.founding)  return 'founding';
  if (position <= TIERS.early_bird) return 'early_bird';
  return 'waitlist';
}

export default function Waitlist() {
  const { t } = useLang();
  // ── Form state ──────────────────────────────────────────────────────────────
  const [step, setStep]         = useState('form'); // 'form' | 'success'
  const [role, setRole]         = useState(null);
  const [consent, setConsent]   = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [entry, setEntry]       = useState(null);
  const [copied, setCopied]     = useState(false);
  const successRef = useRef(null);

  // ── Live counter ────────────────────────────────────────────────────────────
  const [totalCount, setTotalCount]   = useState(null);
  const [foundingLeft, setFoundingLeft] = useState(100);
  const [earlyLeft, setEarlyLeft]     = useState(400);

  // ── Referral code from URL ──────────────────────────────────────────────────
  const urlRef = new URLSearchParams(window.location.search).get('ref');

  // ── Scroll reveal ───────────────────────────────────────────────────────────
  const revealRefs = useRef([]);
  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('wl-visible'); obs.unobserve(e.target); } }),
      { threshold: 0.1 }
    );
    revealRefs.current.forEach(el => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);
  const reveal = (i) => (el) => { revealRefs.current[i] = el; };

  useEffect(() => { loadStats(); }, []);

  async function loadStats() {
    try {
      const { count } = await supabase.from('waitlist').select('*', { count: 'exact', head: true });
      const total = count || 0;
      setTotalCount(total);
      setFoundingLeft(Math.max(0, TIERS.founding - total));
      setEarlyLeft(Math.max(0, TIERS.early_bird - total));
    } catch (_) {}
  }

  // ── Submit ──────────────────────────────────────────────────────────────────
  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!role) { setError(t('waitlist.roleError')); return; }

    const fullName = e.target.full_name.value.trim();
    const email    = e.target.email.value.trim().toLowerCase();
    const country  = e.target.country.value;
    const phone    = e.target.phone.value.trim() || null;
    const howHeard = e.target.how_heard.value || null;
    const feedback = e.target.feedback.value.trim() || null;

    if (!fullName || !email || !country) { setError(t('waitlist.fieldsError')); return; }

    setLoading(true);
    try {
      const { data, error: dbErr } = await supabase
        .from('waitlist')
        .insert({ full_name: fullName, email, role, country, phone, how_heard: howHeard, feedback, marketing_consent: consent, referred_by_code: urlRef || null })
        .select().single();

      if (dbErr) {
        setError(dbErr.code === '23505'
          ? t('waitlist.duplicateError')
          : t('waitlist.networkError'));
        setLoading(false); return;
      }

      setEntry(data);
      setStep('success');
      loadStats();

      // Scroll to success box so user sees it
      setTimeout(() => {
        successRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);

      // Send welcome email
      supabase.functions.invoke('send-waitlist-welcome', {
        body: {
          email:         data.email,
          full_name:     data.full_name,
          position:      data.position,
          tier:          data.tier,
          referral_code: data.referral_code,
        },
      }).catch(() => {}); // fire and forget — don't block UI
    } catch (_) {
      setError(t('waitlist.networkError'));
      setLoading(false);
    }
  }

  function copyReferral() {
    const link = `https://transportily.app/?ref=${entry.referral_code}`;
    navigator.clipboard.writeText(link).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const referralLink = entry ? `https://transportily.app/?ref=${entry.referral_code}` : '';
  const shareText    = encodeURIComponent(`I just joined the Transportily waitlist — the community transport platform connecting Germany 🇩🇪 and Tunisia 🇹🇳! Join me: ${referralLink}`);

  // ── Tier config ─────────────────────────────────────────────────────────────
  const tierConfig = {
    founding:   { label: '🏅 Founding Member', color: 'var(--red)',    bg: 'rgba(232,52,26,0.12)',   border: 'rgba(232,52,26,0.3)',   perk: '50% platform fee off — forever' },
    early_bird: { label: '🐦 Early Bird',       color: 'var(--accent)', bg: 'rgba(232,197,71,0.12)', border: 'rgba(232,197,71,0.3)', perk: '3 months free platform fee' },
    waitlist:   { label: '✅ On the Waitlist',  color: 'var(--green)',  bg: 'rgba(76,175,125,0.1)',  border: 'rgba(76,175,125,0.25)', perk: 'Early access before public launch' },
  };
  const myTier = entry ? tierConfig[getTier(entry.position)] : null;

  return (
    <div className="wl-page">

      {/* App Navbar handles navigation — no duplicate nav needed */}

      {/* ── HERO ────────────────────────────────────────────────────────────── */}
      <section className="wl-hero">
        <div className="wl-hero-glow" />
        <div className="wl-container">
          <div className="wl-hero-flags">🇩🇪 <span className="wl-arrow">⟷</span> 🇹🇳</div>
          <h1 className="wl-hero-title">
            {t('waitlist.hero1')}<br /><em>{t('waitlist.hero2')}</em>
          </h1>
          <p className="wl-hero-sub">{t('waitlist.heroSub')}</p>

          <div className="wl-hero-stats">
            <div className="wl-stat">
              <div className="wl-stat-val red">{t('waitlist.stat1Val')}</div>
              <div className="wl-stat-label">{t('waitlist.stat1Label')}</div>
            </div>
            <div className="wl-stat-divider" />
            <div className="wl-stat">
              <div className="wl-stat-val">{t('waitlist.stat2Val')}</div>
              <div className="wl-stat-label">{t('waitlist.stat2Label')}</div>
            </div>
            <div className="wl-stat-divider" />
            <div className="wl-stat">
              <div className="wl-stat-val">1 <span className="red">{t('waitlist.stat3Val')}</span></div>
              <div className="wl-stat-label">{t('waitlist.stat3Label')}</div>
            </div>
          </div>

          <div className="wl-hero-btns">
            <a href="#signup" className="wl-btn-primary">{t('waitlist.joinBtn')}</a>
            <a href="#how" className="wl-btn-ghost">{t('waitlist.howBtn')}</a>
          </div>

          {totalCount !== null && (
            <div className="wl-live-badge">
              <span className="wl-live-dot" />
              <strong>{totalCount.toLocaleString()}</strong> {t('waitlist.counter')}
            </div>
          )}
        </div>
      </section>

      {/* ── PROBLEM ─────────────────────────────────────────────────────────── */}
      <section className="wl-section" id="problem" ref={reveal(0)}>
        <div className="wl-container">
          <div className="wl-section-header">
            <span className="wl-tag">{t('waitlist.problemTag')}</span>
            <h2>{t('waitlist.problemTitle')}</h2>
            <p>{t('waitlist.problemSub')}</p>
          </div>
          <div className="wl-grid-4">
            {[
              { icon: '💸', title: 'DHL is outrageously expensive', body: '€80+ for 10kg. Official couriers eat 60% of your package\'s value before it even arrives.' },
              { icon: '😬', title: 'Informal trust is a gamble', body: 'Handing your package to a stranger from a WhatsApp group with no tracking, no contract, no recourse.' },
              { icon: '🛃', title: 'Customs is a nightmare', body: 'Unclear rules, confiscated items, unexpected duties. Nobody explains what you can actually send.' },
              { icon: '📦', title: 'Transporters have empty suitcases', body: 'Every week, thousands fly between Germany and Tunisia with unused luggage space that goes to waste.' },
            ].map((c, i) => (
              <div key={i} className="wl-problem-card" ref={reveal(i + 1)}>
                <span className="wl-problem-icon">{c.icon}</span>
                <h3>{c.title}</h3>
                <p>{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ────────────────────────────────────────────────────── */}
      <section className="wl-section wl-section-alt" id="how" ref={reveal(5)}>
        <div className="wl-container">
          <div className="wl-section-header">
            <span className="wl-tag">{t('waitlist.solutionTag')}</span>
            <h2>{t('waitlist.solutionTitle')}</h2>
            <p>{t('waitlist.solutionSub')}</p>
          </div>
          <div className="wl-grid-3">
            {[
              { n: '01', title: 'Transporters post their trips', body: 'Travelling to Tunis? Post your available kg and price. Takes 2 minutes.' },
              { n: '02', title: 'Senders find and book', body: 'Browse upcoming trips, filter by city and date. Book and pay securely — no cash upfront.' },
              { n: '03', title: 'Goods travel safely', body: 'Chat in-app, track the status from collection to delivery. Reviews build trust over time.' },
            ].map((s, i) => (
              <div key={i} className="wl-how-card" ref={reveal(i + 6)}>
                <div className="wl-how-num">{s.n}</div>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PERKS ───────────────────────────────────────────────────────────── */}
      <section className="wl-section" id="perks" ref={reveal(9)}>
        <div className="wl-container">
          <div className="wl-section-header">
            <span className="wl-tag">{t('waitlist.perksTag')}</span>
            <h2>{t('waitlist.perksTitle')}</h2>
            <p>{t('waitlist.perksSub')}</p>
          </div>
          <div className="wl-grid-3">

            {/* Founding */}
            <div className="wl-perk-card wl-perk-founding" ref={reveal(10)}>
              <span className="wl-perk-badge" style={{ color: 'var(--red)', background: 'rgba(232,52,26,0.15)', border: '1px solid rgba(232,52,26,0.3)' }}>🏅 Founding Member</span>
              <div className="wl-perk-num" style={{ color: 'var(--red)' }}>{foundingLeft > 0 ? foundingLeft : '🔒'}</div>
              <div className="wl-perk-num-label">spots remaining</div>
              <ul className="wl-perk-list" style={{ '--check-color': 'var(--red)' }}>
                <li>50% platform fee — for life</li>
                <li>🏅 Founding Member profile badge</li>
                <li>Priority access at launch</li>
                <li>Direct input on new features</li>
                <li>Your name in the app credits</li>
              </ul>
              <div className="wl-progress">
                <div className="wl-progress-bar">
                  <div className="wl-progress-fill" style={{ width: `${Math.min(100, ((TIERS.founding - foundingLeft) / TIERS.founding) * 100)}%`, background: 'var(--red)' }} />
                </div>
              </div>
            </div>

            {/* Early Bird */}
            <div className="wl-perk-card wl-perk-early" ref={reveal(11)}>
              <span className="wl-perk-badge" style={{ color: 'var(--accent)', background: 'rgba(232,197,71,0.15)', border: '1px solid rgba(232,197,71,0.3)' }}>🐦 Early Bird</span>
              <div className="wl-perk-num" style={{ color: 'var(--accent)' }}>{earlyLeft > 0 ? earlyLeft : '🔒'}</div>
              <div className="wl-perk-num-label">spots (positions 101–500)</div>
              <ul className="wl-perk-list" style={{ '--check-color': 'var(--accent)' }}>
                <li>3 months free platform fee</li>
                <li>🐦 Early Bird profile badge</li>
                <li>Priority access at launch</li>
                <li>Beta testing invitations</li>
              </ul>
              <div className="wl-progress">
                <div className="wl-progress-bar">
                  <div className="wl-progress-fill" style={{ width: `${Math.min(100, ((TIERS.early_bird - TIERS.founding - earlyLeft) / 400) * 100)}%`, background: 'var(--accent)' }} />
                </div>
              </div>
            </div>

            {/* Waitlist */}
            <div className="wl-perk-card wl-perk-waitlist" ref={reveal(12)}>
              <span className="wl-perk-badge" style={{ color: 'var(--green)', background: 'rgba(76,175,125,0.12)', border: '1px solid rgba(76,175,125,0.3)' }}>✅ Waitlist</span>
              <div className="wl-perk-num" style={{ color: 'var(--green)' }}>∞</div>
              <div className="wl-perk-num-label">open to everyone</div>
              <ul className="wl-perk-list" style={{ '--check-color': 'var(--green)' }}>
                <li>Access before public launch</li>
                <li>Launch day email notification</li>
                <li>Move up by referring friends</li>
                <li>Community newsletter</li>
              </ul>
              <div className="wl-referral-tip">
                💡 Share your referral link to move up to Founding Member!
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── BOOKING SIMULATOR ───────────────────────────────────────────────── */}
      <BookingSimulator />

      {/* ── SIGNUP FORM ─────────────────────────────────────────────────────── */}
      <section className="wl-section-form" id="signup">
        <div className="wl-container">
          <div className="wl-section-header" ref={reveal(13)}>
            <span className="wl-tag">{t('waitlist.formTag')}</span>
            <h2>{t('waitlist.formTitle')}</h2>
            <p>{t('waitlist.formSub')}</p>
          </div>

          <div className="wl-form-wrap">

            {/* ── Success ── */}
            {step === 'success' && entry && (
              <div className="wl-success" ref={successRef}>
                <div className="wl-success-icon">🎉</div>
                <h3>{t('waitlist.successTitle')}</h3>
                <p>{t('waitlist.successSub')}</p>

                <div className="wl-success-pos">
                  📍 You are <strong>#{entry.position}</strong> on the waitlist
                </div>

                <div className="wl-tier-pill" style={{ color: myTier.color, background: myTier.bg, border: `1px solid ${myTier.border}` }}>
                  {myTier.label} — {myTier.perk}
                </div>

                <div className="wl-ref-box">
                  <div className="wl-ref-label">{t('waitlist.refLabel')}</div>
                  <div className="wl-ref-row">
                    <input className="wl-ref-input" value={referralLink} readOnly />
                    <button className="wl-copy-btn" onClick={copyReferral}>
                      {copied ? t('waitlist.copied') : t('waitlist.copyBtn')}
                    </button>
                  </div>
                  <p className="wl-ref-tip">{t('waitlist.refTip')}</p>
                </div>

                <div className="wl-share-btns">
                  <a href={`https://wa.me/?text=${shareText}`} target="_blank" rel="noreferrer" className="wl-btn-primary" style={{ fontSize: 14 }}>{t('waitlist.shareWa')}</a>
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`} target="_blank" rel="noreferrer" className="wl-btn-ghost" style={{ fontSize: 14 }}>{t('waitlist.shareFb')}</a>
                </div>
              </div>
            )}

            {/* ── Form ── */}
            {step === 'form' && (
              <form className="wl-form" onSubmit={handleSubmit}>
                <div className="wl-form-grid">

                  <div className="wl-field">
                    <label>{t('waitlist.nameLabel')}</label>
                    <input name="full_name" type="text" placeholder="Ahmed Ben Ali" required />
                  </div>

                  <div className="wl-field">
                    <label>{t('waitlist.emailLabel')}</label>
                    <input name="email" type="email" placeholder="you@example.com" required />
                  </div>

                  <div className="wl-field">
                    <label>{t('waitlist.countryLabel')}</label>
                    <select name="country" required>
                      <option value="">Select country…</option>
                      <option>🇩🇪 Germany</option>
                      <option>🇹🇳 Tunisia</option>
                      <option>🇫🇷 France</option>
                      <option>🇧🇪 Belgium</option>
                      <option>🇳🇱 Netherlands</option>
                      <option>🇨🇭 Switzerland</option>
                      <option>🇦🇹 Austria</option>
                      <option>🇸🇪 Sweden</option>
                      <option>Other</option>
                    </select>
                  </div>

                  <div className="wl-field">
                    <label>{t('waitlist.phoneLabel')}</label>
                    <input name="phone" type="tel" placeholder="+49 123 456789" />
                  </div>

                  <div className="wl-field wl-field-full">
                    <label>{t('waitlist.roleLabel')}</label>
                    <div className="wl-role-grid">
                      {[
                        { val: 'sender',      icon: '📦', label: 'Send goods' },
                        { val: 'transporter', icon: '✈️', label: 'Carry goods' },
                        { val: 'both',        icon: '🔄', label: 'Both' },
                      ].map(r => (
                        <button key={r.val} type="button" className={`wl-role-btn${role === r.val ? ' active' : ''}`} onClick={() => setRole(r.val)}>
                          <span className="wl-role-icon">{r.icon}</span>
                          {r.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="wl-field wl-field-full">
                    <label>{t('waitlist.howLabel')}</label>
                    <select name="how_heard">
                      <option value="">Select…</option>
                      <option value="facebook">Facebook</option>
                      <option value="friend">A friend</option>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="instagram">Instagram</option>
                      <option value="tiktok">TikTok</option>
                      <option value="youtube">YouTube</option>
                      <option value="google">Google</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="wl-field wl-field-full">
                    <label>{t('waitlist.feedbackLabel')}</label>
                    <textarea name="feedback" placeholder="e.g. Real-time tracking, ID verification for transporters, customs guidance…" />
                  </div>

                  <div className={`wl-consent${consent ? ' checked' : ''}`} onClick={() => setConsent(v => !v)}>
                    <div className="wl-consent-check">{consent && '✓'}</div>
                    <span>{t('waitlist.consentText')}</span>
                  </div>

                  {error && <div className="wl-error">{error}</div>}

                  <button type="submit" className="wl-submit" disabled={loading}>
                    {loading ? t('waitlist.submitting') : t('waitlist.submitBtn')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────────────── */}
      <section className="wl-section wl-section-alt" ref={reveal(14)}>
        <div className="wl-container">
          <div className="wl-section-header">
            <span className="wl-tag">{t('waitlist.faqTag')}</span>
            <h2>{t('waitlist.faqTitle')}</h2>
          </div>
          <div className="wl-faq">
            {[
              { q: 'Is Transportily free to join?', a: 'Yes. Joining the waitlist is completely free. When we launch, Transportily takes a small 10% platform fee on completed bookings only — no subscription, no hidden fees.' },
              { q: 'When will Transportily launch?', a: 'We are in final testing. Waitlist members will be the first to know — you\'ll get an activation email before we open to the public.' },
              { q: 'How does the referral system work?', a: 'Every person who signs up gets a unique referral link. When someone uses your link, you move up the waitlist — potentially reaching Founding Member even if you joined late.' },
              { q: 'What routes does Transportily cover?', a: 'Launching with Germany ↔ Tunisia. All major German cities (Berlin, Hamburg, Munich, Frankfurt…) and Tunisian cities (Tunis, Sfax, Sousse, Monastir…).' },
              { q: 'Is my data safe?', a: 'Yes. We only collect what\'s necessary. Your data is stored securely, never sold, and you can request deletion at any time. We comply with GDPR.' },
            ].map((f, i) => <FaqItem key={i} q={f.q} a={f.a} />)}
          </div>
        </div>
      </section>

      {/* ── FOOTER ──────────────────────────────────────────────────────────── */}
      <footer className="wl-footer">
        <div className="wl-logo">Transport<span>ily</span></div>
        <div className="wl-footer-links">
          <a href="/terms">Terms</a>
          <a href="/privacy">Privacy</a>
          <a href="/impressum">Impressum</a>
          <a href="mailto:hello@transportily.app">Contact</a>
        </div>
        <p>© 2026 Transportily · Germany ↔ Tunisia community transport</p>
      </footer>

    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`wl-faq-item${open ? ' open' : ''}`} onClick={() => setOpen(v => !v)}>
      <div className="wl-faq-q">{q} <span className="wl-faq-arrow">›</span></div>
      <div className="wl-faq-a">{a}</div>
    </div>
  );
}